/**
 * Placement test — diagnostic that pre-populates `CardStore` so an
 * intermediate user doesn't grind through items they already own.
 *
 * Pure functions, web/mobile-agnostic. The caller (a route component)
 * sequences items through the audio engine, captures rating + responseMs
 * per item, then passes the results to `seedCardsFromPlacement` to mint
 * the initial card store.
 *
 * Phase 2A spec §5.
 */

import { type Card, type CardState } from "./fsrs.js";
import { type CurriculumNode, type Item, MVP_NODES } from "./item-library.js";
import type { CardStore } from "./scheduler.js";

/* ─── public types ────────────────────────────────────────────────────── */

export interface PlacementResult {
  itemId: string;
  /** Bucketed signal — the caller classifies responseMs against the
   *  `<3000ms` / `3000–8000ms` / `>8000ms` thresholds. */
  rating: "skipped" | "wrong" | "slow" | "ok" | "fast";
  responseMs: number;
}

export interface BuildPlacementSessionConfig {
  /** How many items to pull from each node. Default 1. */
  itemsPerNode?: number;
  /** Hard cap on total session length. Default 15. */
  maxItems?: number;
  /**
   * Seed for the per-node representative pick. Same seed → same items,
   * so tests are deterministic. Vary the seed (e.g. `Date.now()`) on
   * retake to give the user a different sampling of items.
   */
  seed?: number;
}

/* ─── placementEligibleNodes ──────────────────────────────────────────── */

/**
 * Tracks the ear-training placement never samples. `guitar` gets its own
 * dedicated placement (deferred); `dictation` exercises use grid input that
 * doesn't fit the quick single-action diagnostic (same reasoning the
 * acquisition bypass-quiz excludes them). Items in these tracks are left as
 * new — conservative under-seeding, consistent with the placement philosophy.
 */
const PLACEMENT_EXCLUDED_TRACKS = new Set<CurriculumNode["track"]>(["guitar", "dictation"]);

/** Curriculum nodes eligible for the ear-training placement pool. */
export function placementEligibleNodes(nodes: CurriculumNode[]): CurriculumNode[] {
  return nodes.filter((n) => !PLACEMENT_EXCLUDED_TRACKS.has(n.track));
}

/** Curriculum nodes for the dedicated guitar placement pool. */
export function guitarPlacementNodes(nodes: CurriculumNode[]): CurriculumNode[] {
  return nodes.filter((n) => n.track === "guitar");
}

/* ─── buildPlacementSession ───────────────────────────────────────────── */

/**
 * Pick ~15 items spanning the curriculum — at most one per node (or
 * `itemsPerNode`), weighted toward foundational nodes (lower prereq
 * depth first) so a beginner doesn't trip on V/vi before do-re-mi has
 * been introduced. Same-depth nodes are sorted by id for stability.
 *
 * Within a node, the representative item is chosen by a seeded hash so
 * reruns can vary while staying deterministic for tests (default seed
 * is 0).
 */
export function buildPlacementSession(
  nodes: CurriculumNode[],
  items: Item[],
  config: BuildPlacementSessionConfig = {},
): Item[] {
  const itemsPerNode = config.itemsPerNode ?? 1;
  const maxItems = config.maxItems ?? 15;
  const seed = config.seed ?? 0;

  const depths = computeDepths(nodes);

  const sortedNodes = [...nodes].sort((a, b) => {
    const da = depths.get(a.id) ?? Number.POSITIVE_INFINITY;
    const db = depths.get(b.id) ?? Number.POSITIVE_INFINITY;
    if (da !== db) return da - db;
    return a.id.localeCompare(b.id);
  });

  const out: Item[] = [];
  for (const node of sortedNodes) {
    if (out.length >= maxItems) break;
    const nodeItems = items.filter((i) => i.nodeId === node.id);
    if (nodeItems.length === 0) continue;
    const picked = pickSeeded(nodeItems, itemsPerNode, seed, node.id);
    for (const item of picked) {
      if (out.length >= maxItems) break;
      out.push(item);
    }
  }
  return out;
}

/* ─── seedCardsFromPlacement ──────────────────────────────────────────── */

const FAST_SEED = { stability: 14, difficulty: 5 } as const;
const OK_SEED = { stability: 7, difficulty: 6 } as const;
/** Slow: state "learning", low stability — engine treats it as still
 *  bedding in. Difficulty mid-range so subsequent reviews aren't
 *  artificially easy or hard. */
const SLOW_SEED = { stability: 1, difficulty: 6 } as const;

/**
 * Given the user's placement results plus the full item catalog, mint
 * a CardStore with FSRS cards for items the user demonstrated mastery
 * on, plus a conservative inheritance for items in the same track they
 * never saw.
 *
 * Spec §5.2 table:
 *   correct+fast  → state "review", stability 14, difficulty 5
 *   correct+ok    → state "review", stability 7,  difficulty 6
 *   correct+slow  → state "learning", low stability (default-fresh)
 *   wrong         → no seed (item stays "new")
 *   skipped       → no seed
 *
 * Track inheritance (spec §5.2 last paragraph): items in nodes the user
 * *didn't* see during placement inherit the average placement of nodes
 * they *did* see in the same track. Bias toward under-seeding so FSRS
 * confirms rather than assumes.
 *
 * Concrete rule applied here: per track, count direct review-state
 * seeds (fast + ok). If the track has at least 2 seen items and at
 * least half were seeded as "review", *unseen* items in that track
 * get a `slow`-equivalent "learning" seed — never a "review" seed.
 * Below that threshold, unseen items are left as new. Reasoning:
 * track-level inheritance is a weaker signal than direct evidence,
 * so it shouldn't promote an item to a 7- or 14-day review interval
 * the user never demonstrated.
 *
 * Accepts `nodes` (defaults to MVP_NODES) so it can resolve item →
 * track for the inheritance step. The spec signature in §5.3 omitted
 * this; the param is optional and back-compatible.
 */
export function seedCardsFromPlacement(
  results: PlacementResult[],
  items: Item[],
  nodes: CurriculumNode[] = MVP_NODES,
  now: Date = new Date(),
): CardStore {
  const cards: CardStore = {};
  const itemById = new Map(items.map((i) => [i.id, i]));
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const trackOfItem = (item: Item): CurriculumNode["track"] | null => {
    const node = nodeById.get(item.nodeId);
    return node?.track ?? null;
  };

  // 1. Direct seeds.
  const seenNodeIds = new Set<string>();
  const trackSeeds = new Map<
    CurriculumNode["track"],
    { reviewCount: number; total: number }
  >();

  for (const result of results) {
    const item = itemById.get(result.itemId);
    if (!item) continue;
    seenNodeIds.add(item.nodeId);

    const track = trackOfItem(item);
    if (track) {
      const t = trackSeeds.get(track) ?? { reviewCount: 0, total: 0 };
      t.total += 1;
      if (result.rating === "fast" || result.rating === "ok") t.reviewCount += 1;
      trackSeeds.set(track, t);
    }

    const card = cardFromRating(result.rating, now);
    if (card) cards[result.itemId] = card;
  }

  // 2. Conservative track inheritance for items in *unseen nodes*.
  for (const item of items) {
    if (cards[item.id]) continue;
    if (seenNodeIds.has(item.nodeId)) continue;
    const track = trackOfItem(item);
    if (!track) continue;
    const stats = trackSeeds.get(track);
    if (!stats || stats.total < 2) continue;
    if (stats.reviewCount * 2 < stats.total) continue;
    cards[item.id] = makeCard(SLOW_SEED.stability, SLOW_SEED.difficulty, "learning", now);
  }

  return cards;
}

/* ─── helpers ─────────────────────────────────────────────────────────── */

/** Distance from this node to the nearest no-prereq root. 0 for roots. */
function computeDepths(nodes: CurriculumNode[]): Map<string, number> {
  const depths = new Map<string, number>();
  const byId = new Map(nodes.map((n) => [n.id, n]));

  const compute = (id: string, visiting: Set<string>): number => {
    const cached = depths.get(id);
    if (cached !== undefined) return cached;
    if (visiting.has(id)) return 0;
    const node = byId.get(id);
    if (!node || node.prerequisites.length === 0) {
      depths.set(id, 0);
      return 0;
    }
    visiting.add(id);
    let best = Number.POSITIVE_INFINITY;
    for (const p of node.prerequisites) {
      const d = compute(p, visiting);
      if (d + 1 < best) best = d + 1;
    }
    visiting.delete(id);
    const final = best === Number.POSITIVE_INFINITY ? 0 : best;
    depths.set(id, final);
    return final;
  };

  for (const n of nodes) compute(n.id, new Set());
  return depths;
}

/** Deterministic pick of `n` items from `arr` using `seed + key` as the hash basis. */
function pickSeeded<T>(arr: T[], n: number, seed: number, key: string): T[] {
  if (n >= arr.length) return [...arr];
  const indexed = arr.map((item, i) => ({ item, h: hashStr(`${key}:${i}`, seed) }));
  indexed.sort((a, b) => a.h - b.h);
  return indexed.slice(0, n).map((x) => x.item);
}

function hashStr(s: string, seed: number): number {
  let h = seed | 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(h ^ s.charCodeAt(i), 0x01000193) | 0) >>> 0;
  }
  return h >>> 0;
}

function cardFromRating(rating: PlacementResult["rating"], now: Date): Card | null {
  switch (rating) {
    case "fast":
      return makeCard(FAST_SEED.stability, FAST_SEED.difficulty, "review", now);
    case "ok":
      return makeCard(OK_SEED.stability, OK_SEED.difficulty, "review", now);
    case "slow":
      return makeCard(SLOW_SEED.stability, SLOW_SEED.difficulty, "learning", now);
    case "wrong":
    case "skipped":
      return null;
  }
}

function makeCard(stability: number, difficulty: number, state: CardState, now: Date): Card {
  const lastReview = now.toISOString();
  // For "review" cards we want the user to come back when the FSRS-default
  // 0.9-retention interval would fire — that's roughly `stability * 1.005`
  // days, i.e. effectively `stability` days. For "learning" we keep it
  // due now so it surfaces in the next session as a new-pool item.
  const dueOffsetDays = state === "review" ? stability : 0;
  const due = new Date(now.getTime() + dueOffsetDays * 86_400_000).toISOString();
  return {
    stability,
    difficulty,
    reps: 1,
    lapses: 0,
    state,
    lastReview,
    due,
  };
}
